import React, { useState } from 'react';
import { Dialog, DialogContent, DialogActions, Box } from '@material-ui/core';
import Slider from '@material-ui/core/Slider';
import Cropper from 'react-easy-crop';
import getResizedImage from 'helpers/image';
import { ImageArea, ImageCrop, ImageCropResult } from 'models';
import { ImageAspectRatio } from 'models/forms';
import SecondaryButton from '../buttons/SecondaryButton';
import PrimaryButton from '../buttons/PrimaryButton';
import useStyles from './style';

interface Props {
  isOpen: boolean;
  src: string;
  aspectRatio: ImageAspectRatio;
  handleAccept: (image: ImageCropResult) => void;
  handleCancel: () => void;
}

const ImageCropper: React.FC<Props> = (props) => {
  const { isOpen, src, aspectRatio, handleAccept, handleCancel } = props;
  const imageAR = aspectRatio === ImageAspectRatio.AR_9_16 ? 9 / 16 : 2 / 3;
  const initialCrop: ImageCrop = { x: 0, y: 0 };
  const initialArea: ImageArea = { width: 0, height: 0, x: 0, y: 0 };
  const classes = useStyles();
  const [zoom, setZoom] = useState(1);
  const [crop, setCrop] = useState(initialCrop);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(initialArea);

  const onCropChange = (value: ImageCrop): void => {
    setCrop(value);
  };

  const onCropComplete = async (
    _: ImageArea,
    areaPixels: ImageArea,
  ): Promise<void> => {
    setCroppedAreaPixels(areaPixels);
  };

  const onZoomChange = (value: number | number[]): void => {
    setZoom(value as number);
  };

  const handleCancelClick = (): void => {
    handleCancel();
  };

  const handleSave = async (): Promise<void> => {
    const resizedImage = await getResizedImage(src, croppedAreaPixels);
    if (resizedImage) handleAccept(resizedImage);
  };

  return (
    <Dialog fullWidth open={isOpen} maxWidth='sm'>
      <DialogContent className={classes.content} dividers>
        <div className={classes.cropContainer}>
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            aspect={imageAR}
            onCropChange={onCropChange}
            onCropComplete={onCropComplete}
            onZoomChange={onZoomChange}
          />
        </div>
        <div className={classes.controls}>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            aria-labelledby='Zoom'
            onChange={(e, value): void => onZoomChange(value)}
          />
        </div>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Box>
          <SecondaryButton onClick={handleCancelClick}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleSave}>Save</PrimaryButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropper;
